import React, { memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Marker } from "react-simple-maps";

const CountryMarker = memo(function CountryMarker({
  country,
  countryDots,
  stateDotCloud,
  isFocused,
  onHover,
}) {
  return (
    <g>
      {/* Invisible hover target */}
      <Marker coordinates={country.center}>
        <circle
          r={isFocused ? 0 : 16}
          fill="transparent"
          style={{ cursor: "pointer" }}
          onMouseEnter={() => onHover(country)}
        />
      </Marker>

      {/* Country-level scatter dots */}
      {countryDots.map((dot, di) => (
        <Marker key={`c-${di}`} coordinates={[dot.cx, dot.cy]}>
          <motion.circle
            r={dot.size}
            fill="#22c55e"
            fillOpacity={isFocused ? 0 : dot.opacity}
            initial={{ scale: 0 }}
            animate={{
              scale: 1,
              fillOpacity: isFocused ? 0 : dot.opacity,
            }}
            transition={{
              scale: { duration: 0.3, delay: di * 0.008 },
              fillOpacity: { duration: 0.2 },
            }}
          />
        </Marker>
      ))}

      {/* State-level dot clouds */}
      <AnimatePresence>
        {isFocused &&
          stateDotCloud?.states.map((sc, si) => {
            const stateData = country.states.find((s) => s.name === sc.name);

            return (
              <g key={sc.name}>
                {/* Scattered dots per state */}
                {sc.dots.map((dot, di) => (
                  <Marker key={`sd-${si}-${di}`} coordinates={[dot.cx, dot.cy]}>
                    <motion.circle
                      r={dot.size}
                      fill="#22c55e"
                      fillOpacity={dot.opacity}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: dot.opacity }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{
                        duration: 0.25,
                        delay: di * 0.012 + si * 0.05,
                      }}
                    />
                  </Marker>
                ))}

                {/* State label + count */}
                {stateData && (
                  <Marker coordinates={sc.center}>
                    {/* Pulse ring */}
                    <motion.circle
                      r={3.5}
                      fill="transparent"
                      stroke="#22c55e"
                      strokeOpacity={0.4}
                      strokeWidth={0.7}
                      initial={{ scale: 0 }}
                      animate={{
                        scale: [0.8, 1.8, 0.8],
                        opacity: [0.6, 0, 0.6],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: si * 0.2,
                      }}
                    />
                    {/* Centre dot */}
                    <motion.circle
                      r={1.5}
                      fill="#22c55e"
                      stroke="#fff"
                      strokeWidth={0.4}
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      exit={{ scale: 0, opacity: 0 }}
                      transition={{ duration: 0.2, delay: si * 0.05 }}
                    />
                    {/* State name */}
                    <motion.text
                      textAnchor="middle"
                      dy={-3.5}
                      fontSize={2.6}
                      fill="#ffffff"
                      fillOpacity={0.85}
                      fontFamily="Geist, sans-serif"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: 0.12 + si * 0.05 }}
                      style={{ pointerEvents: "none" }}
                    >
                      {stateData.name}
                    </motion.text>
                    {/* User count */}
                    <motion.text
                      textAnchor="middle"
                      dy={-0.5}
                      fontSize={2.1}
                      fill="#E5B978"
                      fillOpacity={0.9}
                      fontFamily="Geist, sans-serif"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.2, delay: 0.17 + si * 0.05 }}
                      style={{ pointerEvents: "none" }}
                    >
                      {stateData.count.toLocaleString()}
                    </motion.text>
                  </Marker>
                )}
              </g>
            );
          })}
      </AnimatePresence>
    </g>
  );
});

export default CountryMarker;
